import { Grid, MenuItem } from '@material-ui/core';
import { useFormikContext, Field } from 'formik';
import React, { useEffect } from 'react';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import { CurrencyId } from 'v2/interfaces/financial';

import type { IBanklist, INewAccount, IOperationList } from 'v2/interfaces/financial';

const currencies = [
  {
    value: CurrencyId.BRL,
    label: 'R$',
  },
  {
    value: CurrencyId.USD,
    label: '$',
  },
];

const accounts = [
  {
    value: 'CC',
    label: 'Conta Corrente',
  },
  {
    value: 'CP',
    label: 'Conta Poupança',
  },
];

interface IProps {
  banks: IBanklist[];
  operations: IOperationList[];
  isForeign: boolean;
  isLoadingBanks: boolean;
  isLoadingOperations: boolean;
  isRefundAccount?: boolean;
}

const NewAccountForm = ({ banks, operations, isLoadingBanks, isLoadingOperations, isForeign, isRefundAccount }: IProps) => {
  const { values, setFieldValue } = useFormikContext<INewAccount>();

  const isBRLSelected = values.currency === CurrencyId.BRL;
  const isUSDSelected = values.currency === CurrencyId.USD;
  const caixaEconomicaId = '104';

  const pathName = window.location.pathname;
  const isRefundAccountSelected = isRefundAccount || pathName.includes('bank-account-refund');

  useEffect(() => {
    setFieldValue('currency', isForeign ? CurrencyId.USD : CurrencyId.BRL);
  }, [isForeign, setFieldValue]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <Field select name='currency' label='Moeda' fullWidth={true} component={TextFormField}>
          {currencies.map((option) => (
            <MenuItem key={option.value} value={option.value} disabled={(option.value === 2 && !isForeign) || (option.value === 1 && isForeign)}>
              {option.label}
            </MenuItem>
          ))}
        </Field>
      </Grid>

      {isBRLSelected && (
        <Grid item xs={12} sm={8}>
          <Field select name='type' label='Tipo de conta' fullWidth component={TextFormField}>
            {accounts.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Field>
        </Grid>
      )}

      <Grid item xs={12} sm={isBRLSelected ? 12 : 8}>
        <Field
          name='name'
          label='Correntista'
          placeholder='Nome do correntista'
          helperText={!isRefundAccountSelected ? '* Só é permitido o cadastro de conta pertencente ao responsável pelo cadastro na Eduzz' : ''}
          component={TextFormField}
          disabled={!isRefundAccountSelected}
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12}>
        {isBRLSelected && (
          <Field
            name='document_number'
            label='Documento de identificação'
            placeHolder='CPF/CNPJ'
            fullWidth
            mask='document'
            component={TextFormField}
            inputProps={{
              maxLength: 25,
            }}
          />
        )}
        {isUSDSelected && (
          <Field
            name='document_number'
            label='FEI/EIN Number'
            placeHolder='FEI/EIN'
            component={TextFormField}
            fullWidth
            inputProps={{
              maxLength: 25,
            }}
          />
        )}
      </Grid>

      <Grid item xs={12} sm={12}>
        <Field select name='bank_id' label='Banco' fullWidth disabled={isLoadingBanks} component={TextFormField}>
          <MenuItem value='not-selected' disabled selected>
            {isLoadingBanks ? 'Carregando...' : 'Selecione um banco'}
          </MenuItem>

          {banks?.map((bank) => (
            <MenuItem key={bank.id} value={bank.id}>
              {bank.value}
            </MenuItem>
          ))}
        </Field>
      </Grid>

      {values.bank_id === caixaEconomicaId && (
        <Grid item xs={12} sm={12}>
          <Field select name='operation_id' label='Operações Caixa Econômica' fullWidth disabled={isLoadingOperations} component={TextFormField}>
            <MenuItem value='not-selected' disabled selected>
              {isLoadingOperations ? 'Carregando...' : 'Selecione uma Operação'}
            </MenuItem>

            {operations?.map((operation) => (
              <MenuItem key={operation.id} value={operation.id}>
                {operation.id + ' - ' + operation.operation}
              </MenuItem>
            ))}
          </Field>
        </Grid>
      )}

      <Grid item xs={12} sm={isBRLSelected ? 4 : 6}>
        <Field
          name='agency'
          label={isBRLSelected ? 'Agência' : 'ABA Routing Number'}
          fullWidth
          mask='numberandzero'
          component={TextFormField}
          inputProps={{
            maxLength: 20,
          }}
        />
      </Grid>

      {isBRLSelected && (
        <Grid item xs={12} sm={2}>
          <Field
            name='agency_digit'
            label='Dígito'
            fullWidth
            component={TextFormField}
            mask='singledigit'
            inputProps={{
              maxLength: 1,
            }}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={isBRLSelected ? 4 : 6}>
        <Field
          name='account'
          label={isBRLSelected ? 'Conta' : 'Account Number'}
          component={TextFormField}
          mask='numberandzero'
          fullWidth
          inputProps={{
            maxLength: 20,
          }}
        />
      </Grid>

      {isBRLSelected && (
        <Grid item xs={12} sm={2}>
          <Field
            name='account_digit'
            label='Dígito'
            fullWidth
            component={TextFormField}
            mask={values.bank_id === '001' ? 'bankaccountdigit' : 'numberandzero'}
            inputProps={{
              maxLength: 1,
            }}
          />
        </Grid>
      )}

      {isUSDSelected && (
        <Grid item xs={12} sm={12}>
          <Field
            name='notes'
            label='Observações'
            fullWidth
            component={TextFormField}
            inputProps={{
              maxLength: 255,
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default NewAccountForm;
